import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { Symbol } from "./Symbol";
import { Symbols } from "./Symbols";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Tweens } from "../Components/Effects/Tweens";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ReelConfigurationAttributes } from "../Components/Configuration";

/**
 * Class reel represent reel symbols rotation.
 * @class Reel
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Reel extends BaseElement {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly ReelStopped: string = "reelStopped";

    //******************************************************************************************************************
    //  VARIABLES (CONFIGURE)
    //******************************************************************************************************************

    protected preRoll: number;

    protected preRollSpeed: number;

    protected postRoll: number;

    protected postRollSpeed: number;

    protected speed: number;

    protected forceSpeed: number;

    //******************************************************************************************************************
    //  VARIABLES (INITIALIZE)
    //******************************************************************************************************************

    protected previousReel: Reel;

    protected nextReel: Reel;

    protected index: number;

    protected symbols: Symbols;

    protected strip: Array<number>;

    protected positionX: number;

    protected positionY: number;

    protected symbolCount: number;

    protected symbolTopCount: number;

    protected symbolBottomCount: number;

    protected symbolWidth: number;

    protected symbolHeight: number;

    protected startDelay: number;

    protected stopDelay: number;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected stopTimer: FrameTimerEventEmitter;

    public currentSymbols: Array<Symbol>;

    protected data: Communication.Game.IReel;

    public isStopping: boolean;

    protected stoppingCounter: number;

    protected shouldBeStopping: boolean;

    protected isTeasing: boolean;

    protected teasingSymbols: Array<number>;

    protected rollSpeed: number;

    protected stripIndex: number;

    //******************************************************************************************************************
    //  CONFIGURE
    //******************************************************************************************************************

    public configure(node: Element): void {
        this.preRoll = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL))
            : 180;
        this.preRollSpeed = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED))
            : 10;
        this.postRoll = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL))
            : 180;
        this.postRollSpeed = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED))
            : 5;
        this.speed = node.hasAttribute(ReelConfigurationAttributes.SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.SPEED))
            : 3;
        this.forceSpeed = node.hasAttribute(ReelConfigurationAttributes.FORCE_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.FORCE_SPEED))
            : 2;
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        previousReel: Reel,
        nextReel: Reel,
        index: number,
        symbols: Symbols,
        strip: Array<number>,
        positionX: number,
        positionY: number,
        symbolCount: number,
        symbolTopCount: number,
        symbolBottomCount: number,
        symbolWidth: number,
        symbolHeight: number,
        startDelay: number,
        stopDelay: number,
    ): void {
        this.previousReel = previousReel;
        this.nextReel = nextReel;
        this.index = index;
        this.symbols = symbols;
        this.strip = strip;
        this.positionX = positionX;
        this.positionY = positionY;
        this.symbolCount = symbolCount;
        this.symbolTopCount = symbolTopCount;
        this.symbolBottomCount = symbolBottomCount;
        this.symbolWidth = symbolWidth;
        this.symbolHeight = symbolHeight;
        this.startDelay = startDelay;
        this.stopDelay = stopDelay;

        this.stopTimer = CreateFrameTimer("reel");
        this.stopTimer.on(FrameTimerEvents.Complete, this.onStopTimerComplete.bind(this));

        this.currentSymbols = [];
    }

    //******************************************************************************************************************
    //  SET DATA
    //******************************************************************************************************************

    public setData(data: Communication.Game.IReel) {
        this.data = data;
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS
    //******************************************************************************************************************

    public showSymbols(): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

            this.addChild(this.currentSymbols[0]);
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }
    }

    //******************************************************************************************************************
    //  SPIN START
    //******************************************************************************************************************

    public spinStart(): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.rollSpeed = this.speed;
        this.stripIndex = (this.data.index - this.symbolTopCount + this.strip.length) % this.strip.length;

        this.spinPreRoll();
    }

    //******************************************************************************************************************
    //  SPIN STOP
    //******************************************************************************************************************

    public spinStop(force: boolean = false, customDelay: number = 0): void {
        this.stopTimer.stop();

        if (force) {
            this.shouldBeStopping = true;
            this.rollSpeed = this.forceSpeed;
        } else {
            this.stopTimer.start(customDelay !== 0 ? customDelay : this.stopDelay);
        }
    }

    protected onStopTimerComplete(): void {
        this.shouldBeStopping = true;
        this.isStopping = true;
    }

    public getStopDuration(): number {
        const stoppingSymbols = this.symbolTopCount + this.symbolCount + this.symbolBottomCount - 1;
        return this.stopDelay + stoppingSymbols * this.speed + this.postRollSpeed * 2;
    }

    //******************************************************************************************************************
    //  SPIN TEASE
    //******************************************************************************************************************

    public spinTease(symbols: Array<number>, customDelay: number = 0): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = true;
        this.teasingSymbols = symbols;

        this.stopTimer.stop();
        this.stopTimer.start(customDelay !== 0 ? customDelay : this.stopDelay);
    }

    //******************************************************************************************************************
    //  SPIN PRE ROLL
    //******************************************************************************************************************

    protected spinPreRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected spinRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.postRoll, this.positionY],
            tweenDuration: this.postRollSpeed * 2,
            callbackFunction: this.spinComplete,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN COMPLETE
    //******************************************************************************************************************

    protected spinComplete(): void {
        Tweens.removeTweens(this, "position");

        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        this.emit(Reel.ReelStopped, this.index);
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        return this.symbols.getSymbol(symbolId, symbolCase);
    }

    protected returnSymbol(symbol: Symbol): void {
        this.symbols.returnSymbol(symbol);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Reel.ts
 **/